import React, { useState, useMemo } from "react";
import styles from "./DiamondsListCard.module.css";
import PropTypes from "prop-types";

import DiamondInfoItem from "../DiamondInfoItem";
import ViewByModal from "../ViewByModal";
import Card from "../../Shared/Card";
import Button from "../../../shared/Button";
import ProxyVoteIcon from "ManagementTeam/Shared/ProxyVoteIcon";
import GreenCheckmark from "ManagementTeam/Shared/GreenCheckmark";
import { useMeetings } from "ManagementTeam/CalendarPage/useMeetings";
import Auth from "shared/@auth";

/**
 * Component that displays the Diamonds List Card. Can be sorted by last name, gar points, access level, pin level, and check-in status
 * Check-in sorting includes an option to check in by proxy.
 * By default, list is sorted alphabetically
 */

export const DiamondsListCard = ({
  meetingId,
  diamonds,
  onClickProfile,
  option,
  setOption,
  isHideInfo,
}) => {
  const { hasPermission } = Auth.useContainer();
  const readOnly = !hasPermission("management-team.admins");

  const [open, setIsOpen] = useState(false);
  const [_diamonds, setDiamonds] = useState([]);
  const [proxyCheck, setProxyCheck] = useState(false);
  const [proxyCheckDiamonds, setProxyCheckDiamonds] = useState([]);
  const [showProxy, setShowProxy] = useState(false);
  const { meetingCheckInProxyPut } = useMeetings();
  useMemo(() => {
    if (option === "Current GAR Points") {
      setDiamonds(_diamonds.sort((a, b) => b.garPoints - a.garPoints));
    } else {
      setDiamonds(() =>
        diamonds.sort((a, b) =>
          a.lastName > b.lastName ? 1 : b.lastName > a.lastName ? -1 : 0
        )
      );
    }
  }, [diamonds]);

  const accessCounts = {};
  const pinCounts = {
    crowns: 0,
    triples: 0,
    doubles: 0,
    executives: 0,
    diamonds: 0,
  };
  var checkedCount = 0;

  _diamonds.map((e) => {
    if (e.pinLevel) {
      e.pinLevel.includes("Crown") && pinCounts.crowns++;
      e.pinLevel.includes("Triple Diamond") && pinCounts.triples++;
      e.pinLevel.includes("Double Diamond") && pinCounts.doubles++;
      e.pinLevel.includes("Executive Diamond") && pinCounts.executives++;
      e.pinLevel.match(/^Diamond$/) && pinCounts.diamonds++;
    }
    accessCounts[e.accessLevel] = accessCounts[e.accessLevel]
      ? accessCounts[e.accessLevel] + 1
      : 1;
    (e.checkedIn || e.proxyCheckedIn) && checkedCount++;
    return e;
  });

  const handleChange = (e) => {
    if (e === "Alphabetical") {
      setDiamonds(
        _diamonds.sort((a, b) =>
          a.lastName > b.lastName ? 1 : b.lastName > a.lastName ? -1 : 0
        )
      );
    } else if (e === "Current GAR Points") {
      setDiamonds(_diamonds.sort((a, b) => b.garPoints - a.garPoints));
    }
  };

  const onCheckIn = () => {
    var newDiamonds = _diamonds;
    newDiamonds.map((diamond, index) => {
      if (proxyCheckDiamonds.includes(index))
        newDiamonds[index].proxyCheckedIn = true;
    });
    const payload = {
      id: meetingId,
      proxyUsers: proxyCheckDiamonds.map((idx) => newDiamonds[idx].userId),
    };
    meetingCheckInProxyPut.mutateAsync(payload);
    setDiamonds(newDiamonds);
  };

  // error message for case of empty/undefined/null arrays
  const Error = () => {
    return (
      <div className={styles.error}>API Error, please try again later.</div>
    );
  };

  // Children
  const Children = () => {
    return (
      <>
        {_diamonds !== null &&
        _diamonds !== undefined &&
        _diamonds.length > 0 ? (
          option === "Management Team Group" ? (
            // MEMBERS SORTED BY ACCESS LEVEL /////
            <>
              <div className={styles.groupTitle}>
                Executive Committee (
                {accessCounts["EC"] ? accessCounts["EC"] : 0})
              </div>
              <div className={styles.diamondsList}>
                {_diamonds.map((diamond, index) =>
                  diamond.accessLevel === "EC" ||
                  diamond.accessLevel === "MANAGER" ? (
                    <div key={index} className={styles.child}>
                      <DiamondInfoItem
                        diamondInfo={diamond}
                        onClickProfile={onClickProfile}
                        isHideInfo={isHideInfo}
                      />
                    </div>
                  ) : null
                )}
              </div>
              <div className={styles.groupTitle}>
                Voting Members ({accessCounts["MT1"] ? accessCounts["MT1"] : 0})
              </div>
              <div className={styles.diamondsList}>
                {_diamonds.map((diamond, index) =>
                  diamond.accessLevel === "MT1" ? (
                    <div key={index} className={styles.child}>
                      <DiamondInfoItem
                        diamondInfo={diamond}
                        onClickProfile={onClickProfile}
                        isHideInfo={isHideInfo}
                      />
                    </div>
                  ) : null
                )}
              </div>
              <div className={styles.groupTitle}>
                Non-Voting Members (
                {(accessCounts["MT2"] ? accessCounts["MT2"] : 0) +
                  (accessCounts["GUEST"] ? accessCounts["GUEST"] : 0)}
                )
              </div>
              <div className={styles.diamondsList}>
                {_diamonds.map((diamond, index) =>
                  diamond.accessLevel === "MT2" ||
                  diamond.accessLevel === "GUEST" ? (
                    <div key={index} className={styles.child}>
                      <DiamondInfoItem
                        diamondInfo={diamond}
                        onClickProfile={onClickProfile}
                        isHideInfo={isHideInfo}
                      />
                    </div>
                  ) : null
                )}
              </div>
            </>
          ) : option === "Pin Level (current wearing)" ? (
            // MEMBERS SORTED BY PIN /////
            <>
              <div className={styles.groupTitle}>
                Crowns ({pinCounts.crowns})
              </div>
              <div className={styles.diamondsList}>
                {_diamonds.map((diamond, index) =>
                  diamond.pinLevel.includes("Crown") ? (
                    <div key={index} className={styles.child}>
                      <DiamondInfoItem
                        diamondInfo={diamond}
                        onClickProfile={onClickProfile}
                        isHideInfo={isHideInfo}
                      />
                    </div>
                  ) : null
                )}
              </div>
              <div className={styles.groupTitle}>
                Triple Diamonds ({pinCounts.triples})
              </div>
              <div className={styles.diamondsList}>
                {_diamonds.map((diamond, index) =>
                  diamond.pinLevel.includes("Triple Diamond") ? (
                    <div key={index} className={styles.child}>
                      <DiamondInfoItem
                        diamondInfo={diamond}
                        onClickProfile={onClickProfile}
                        isHideInfo={isHideInfo}
                      />
                    </div>
                  ) : null
                )}
              </div>
              <div className={styles.groupTitle}>
                Double Diamonds ({pinCounts.doubles})
              </div>
              <div className={styles.diamondsList}>
                {_diamonds.map((diamond, index) =>
                  diamond.pinLevel.includes("Double Diamond") ? (
                    <div key={index} className={styles.child}>
                      <DiamondInfoItem
                        diamondInfo={diamond}
                        onClickProfile={onClickProfile}
                        isHideInfo={isHideInfo}
                      />
                    </div>
                  ) : null
                )}
              </div>
              <div className={styles.groupTitle}>
                Executive Diamonds ({pinCounts.executives})
              </div>
              <div className={styles.diamondsList}>
                {_diamonds.map((diamond, index) =>
                  diamond.pinLevel.includes("Executive Diamond") ? (
                    <div key={index} className={styles.child}>
                      <DiamondInfoItem
                        diamondInfo={diamond}
                        onClickProfile={onClickProfile}
                        isHideInfo={isHideInfo}
                      />
                    </div>
                  ) : null
                )}
              </div>
              <div className={styles.groupTitle}>
                Diamonds ({pinCounts.diamonds})
              </div>
              <div className={styles.diamondsList}>
                {_diamonds.map((diamond, index) =>
                  diamond.pinLevel.match(/^Diamond$/) ? (
                    <div key={index} className={styles.child}>
                      <DiamondInfoItem
                        diamondInfo={diamond}
                        onClickProfile={onClickProfile}
                        isHideInfo={isHideInfo}
                      />
                    </div>
                  ) : null
                )}
              </div>
            </>
          ) : option === "Checked In" ? (
            // SHOW CHECK IN STATUS WITH PROXY VOTE BUTTON /////
            <>
              {!proxyCheck ? (
                <>
                  <div className={styles.buttonContainer}>
                    {!readOnly && (
                      <Button
                        children="Proxy Check In"
                        className={styles.button}
                        onClick={() => setProxyCheck(!proxyCheck)}
                      />
                    )}
                  </div>
                  <div className={styles.diamondsList}>
                    {_diamonds.map((diamond, index) =>
                      diamond.checkedIn && !diamond.proxyCheckedIn ? (
                        <div key={index} className={styles.child}>
                          <DiamondInfoItem
                            diamondInfo={diamond}
                            onClickProfile={onClickProfile}
                            isHideInfo={isHideInfo}
                          />
                        </div>
                      ) : null
                    )}
                    {_diamonds.map((diamond, index) =>
                      diamond.proxyCheckedIn ? (
                        <div key={index} className={styles.child}>
                          <DiamondInfoItem
                            diamondInfo={diamond}
                            onClickProfile={onClickProfile}
                            isHideInfo={isHideInfo}
                          />
                        </div>
                      ) : null
                    )}
                    {_diamonds.map((diamond, index) =>
                      !diamond.checkedIn && !diamond.proxyCheckedIn ? (
                        <div key={index} className={styles.child}>
                          <DiamondInfoItem
                            diamondInfo={diamond}
                            onClickProfile={onClickProfile}
                            isHideInfo={isHideInfo}
                          />
                        </div>
                      ) : null
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.buttonContainer}>
                    <Button
                      children="Cancel"
                      className={styles.cancelButton}
                      onClick={() => {
                        setProxyCheck(!proxyCheck);
                        setShowProxy(false);
                        setProxyCheckDiamonds([]);
                      }}
                    />
                    <Button
                      children="Submit Check In"
                      className={styles.submitButton}
                      onClick={() => {
                        onCheckIn();
                        setProxyCheck(!proxyCheck);
                      }}
                    />
                  </div>
                  <div className={styles.diamondsList}>
                    {_diamonds.map((diamond, index) =>
                      !diamond.checkedIn && !diamond.proxyCheckedIn ? (
                        <>
                          <div className={styles.noVoteChild}>
                            <DiamondInfoItem
                              diamondInfo={diamond}
                              onClickProfile={onClickProfile}
                              isHideInfo={isHideInfo}
                            />
                            {!diamond.proxyCheckedIn ? (
                              <>
                                <Button
                                  key={index}
                                  children="Check In"
                                  className={styles.checkButton}
                                  style={{
                                    display: showProxy[`${index}`]
                                      ? "none"
                                      : "inline",
                                  }}
                                  onClick={() => {
                                    setProxyCheckDiamonds((prev) => [
                                      ...prev,
                                      index,
                                    ]);
                                    setShowProxy((prevState) => ({
                                      ...showProxy,
                                      [index]: !prevState[index],
                                    }));
                                  }}
                                />
                                <div
                                  key={index}
                                  className={styles.proxyCheck}
                                  style={{
                                    display: showProxy[`${index}`]
                                      ? "inline"
                                      : "none",
                                  }}
                                >
                                  <GreenCheckmark />
                                  <ProxyVoteIcon />
                                </div>
                              </>
                            ) : null}
                          </div>
                        </>
                      ) : null
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            // DEFAULT VIEW FOR ALPHABETICAL AND GAR POINT SORTING /////
            <div className={styles.diamondsList}>
              {_diamonds.map((diamond, index) => (
                <div key={index} className={styles.child}>
                  <DiamondInfoItem
                    diamondInfo={diamond}
                    onClickProfile={onClickProfile}
                    isHideInfo={isHideInfo}
                  />
                </div>
              ))}
            </div>
          )
        ) : (
          <Error />
        )}
      </>
    );
  };

  return (
    <>
      <div
        className={styles.modalContainer}
        style={{ display: open ? "inline" : "none" }}
      >
        <ViewByModal
          selectedOption={option}
          onCollapse={() => setIsOpen(!open)}
          onChange={(e) => {
            setOption(e);
            setIsOpen(!open);
            handleChange(e);
          }}
        />
      </div>
      <Card
        header={`Diamonds - ${
          option === "Checked In"
            ? `Checked In (${checkedCount}/${_diamonds.length})`
            : option
        }`}
        onClick={() => setIsOpen(!open)}
        btnTitle="View Options"
        children={<Children />}
      />
    </>
  );
};

DiamondsListCard.propTypes = {
  // Array of diamond objects (firstName-String, lastName-String, photo-String, pinLevel-String, garPoints-Number, accessLevel-String, checkedIn-Bool, proxyCheckedIn-Bool)
  diamonds: PropTypes.array,

  // Function to save new diamond information
  onSaveUser: PropTypes.func,
};

DiamondsListCard.defaultProps = {
  diamonds: [],
};
